import { usePrescreeningData } from '@/context/pre-screening-context';
import type { PatchPatientForInstantConsultationReply } from '@/hooks/useInstantConsultRequest';
import { useSmsVerification } from '@/hooks/useSmsVerification';

import { CtaButton, CtaButtonText } from '@/pages/components/styled/Cta';

type ResendSmsButtonProps = {
  onSubmit: () => Promise<PatchPatientForInstantConsultationReply>;
};

const MAX_RATE_LIMIT_INDEX = 4;

const constructLabel = (rateLimitIndex: number, millisecondsLeft: number) => {
  if (rateLimitIndex >= MAX_RATE_LIMIT_INDEX) {
    return 'Resend attempts exceeded';
  }

  if (millisecondsLeft > 0) {
    return 'You can retry again shortly';
  }

  return `Resend SMS`;
};

export const ResendSmsButton = ({ onSubmit }: ResendSmsButtonProps) => {
  const { startSmsVerificationCountdown, millisecondsLeft } = useSmsVerification();
  const { preScreeningData } = usePrescreeningData();

  const label = constructLabel(preScreeningData?.smsVerification.rateLimitIndex ?? 0, millisecondsLeft);

  const onHandleResendSms = async () => {
    const response = await onSubmit();
    startSmsVerificationCountdown(response?.data?.retryAfterMs || 0);
  };

  return (
    <CtaButton
      type="button"
      color="primary"
      size="large"
      variant="contained"
      onClick={onHandleResendSms}
      disabled={millisecondsLeft > 0}
      breakpoint="sm"
      data-testid="resend-sms-button"
    >
      <CtaButtonText breakpoint="sm" sx={{ fontWeight: 'bold' }}>
        {label}
      </CtaButtonText>
    </CtaButton>
  );
};
